import React, { useEffect, useMemo, useState } from 'react';
import { Tag, Form, FormInstance } from 'antd';
import { StarIcon } from '../../../../assets/icons/icons';
import { useToken } from '../../../../common/helper';
import { CustomShipmentText } from '../../../../components';
import {
  AllNotifyViaOptions,
  NotifyViaOption,
} from '../../../../common/static_data';

interface NotifyViaPillsProps {
  notificationType: 'both' | 'ai' | 'non-ai';
  form: FormInstance;
  setNotificationMethod: (method: string) => void;
  resetOnChange?: (method: string) => void;
  text?: string;
  alertType?: string;
  disabledMethods?: Array<NotifyViaOption['value']>;
}
/**
 * NotifyViaPills component displays notification method options as clickable pills/tags
 *
 * @param notificationType - Type of notification to display: 'both', 'ai', or 'non-ai'
 * @param form - Ant Design form instance
 * @param setNotificationMethod - Callback function to update the selected notification method
 */
const EMPTY_DISABLED_METHODS: Array<NotifyViaOption['value']> = [];
const NotifyViaPills: React.FC<NotifyViaPillsProps> = ({
  notificationType,
  form,
  setNotificationMethod,
  resetOnChange,
  text = 'Notify via',
  alertType,
  disabledMethods = EMPTY_DISABLED_METHODS,
}) => {
  const { decodedUserToken } = useToken();
  const decodedToken = decodedUserToken();

  const notificationM = Form.useWatch('notification_method', form);

  const aiOneTimePermissions =
    !!decodedToken?.ai_enable && !!decodedToken?.one_time_messages_enabled;

  const [pillsArray, setPillsArray] = useState<NotifyViaOption[]>([]);

  const activeOptionText = useMemo(() => {
    return pillsArray.find((p) => p.value === notificationM)?.text || '';
  }, [pillsArray, notificationM]);

  useEffect(() => {
    const newPills = AllNotifyViaOptions.filter(
      (x) => x.type === 'both' || x.type === notificationType
    ).map((pill) => {
      const isForcedDisabled = disabledMethods.includes(pill.value);

      if (
        pill.type === 'ai' &&
        (pill.value === 'AI_EMAIL' || pill.value === 'AI_CALL')
      ) {
        return {
          ...pill,
          isDisabled: isForcedDisabled || !aiOneTimePermissions,
        };
      }

      if (
        (pill.value === 'MOBILE' || pill.value === 'WHATSAPP') &&
        (alertType === 'AI_ACTION' || alertType === 'TEMPERATURE_GXP')
      ) {
        return { ...pill, isDisabled: true };
      }

      return { ...pill, isDisabled: isForcedDisabled || !!pill.isDisabled };
    });

    setPillsArray(newPills);
  }, [notificationType, alertType, aiOneTimePermissions, disabledMethods]);

  const handleNotifyVia = (item: NotifyViaOption) => {
    form.setFieldsValue({ notification_method: item.value });
    setNotificationMethod(item.value);
    resetOnChange?.(item.value);
  };

  return (
    <div>
      <CustomShipmentText
        text={text}
        fontSize='14px'
        fontWeight={600}
        marginBottom='3px'
      />

      <Form.Item
        name='notification_method'
        rules={[
          { required: true, message: 'Please select a notification option' },
        ]}
      >
        {pillsArray.map((item) => (
          <Tag
            key={item.value}
            onClick={!item.isDisabled ? () => handleNotifyVia(item) : undefined}
            className={`notify-via-pills ${notificationM === item.value ? 'active' : ''} ${item.isDisabled ? 'disabled' : ''}`}
          >
            {item.label}
            {item.icon && <StarIcon />}
          </Tag>
        ))}
      </Form.Item>

      {activeOptionText && (
        <p
          className='font-12 fw-400 lh-20'
          style={{ color: 'var(--text-secondary)', marginTop: '2px' }}
        >
          {activeOptionText}
        </p>
      )}
    </div>
  );
};

export default NotifyViaPills;
